// TeamManagementPage — real team data (direct reports + who's out), KaziPeople
// design system (no hardcoded mock, no primary-* colors).
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/features/shared/components/ui/avatar';
import { Input } from '@/features/shared/components/ui/input';
import { Users, Search, Mail, Phone, CalendarOff, Loader2, Building2, UserCheck } from 'lucide-react';
import { getMySubordinates, getDepartments, getTeamOut, type Employee } from '@/lib/hrApi';

type Status = 'active' | 'on_leave';
interface Member {
  id: string;
  userId?: string | null;
  name: string;
  position?: string;
  department?: string;
  email: string;
  phone?: string | null;
  avatar?: string | null;
  status: Status;
}

const fullName = (e: Employee) => `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email;
const initials = (n: string) => n.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

export const TeamManagementPage: React.FC = () => {
  const { t } = useTranslation('manager');
  const navigate = useNavigate();
  const location = useLocation();
  // App base = current path minus the trailing "/team" segment, so we land on
  // the tenant-scoped /employees/:id detail regardless of the URL prefix.
  const goToEmployee = (id: string) => {
    const base = location.pathname.replace(/\/[^/]+\/?$/, '');
    navigate(`${base}/employees/${id}`);
  };
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | Status>('all');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        // The server resolves the team (direct reports) and who in it is out —
        // not the whole company's directory and leave filtered in the browser.
        const [reports, depts, out] = await Promise.all([
          getMySubordinates(false).catch(() => [] as Employee[]),
          getDepartments().catch(() => ({ items: [] as any[] })),
          // Out TODAY (days_ahead 0). This was a 30-day window, so anyone with
          // leave booked next month already showed "On leave".
          getTeamOut(0, false, 'direct').catch(() => [] as any[]),
        ]);
        const deptName = new Map<string, string>((depts.items || []).map((d: any) => [d.id, d.name]));
        // employee_id on the team-out feed is the user id (matches Employee.user_id).
        const outUserIds = new Set<string>((Array.isArray(out) ? out : []).map((r: any) => String(r.employee_id)));
        const mapped: Member[] = reports.map((e) => ({
          id: e.id,
          userId: e.user_id,
          name: fullName(e),
          position: e.position,
          department: e.department_id ? deptName.get(e.department_id) : undefined,
          email: e.email,
          phone: e.phone,
          avatar: e.profile_picture_url,
          // From the leave dates only — the stored `OnLeave` could outlive the leave.
          status: e.user_id && outUserIds.has(String(e.user_id)) ? 'on_leave' : 'active',
        }));
        if (active) setMembers(mapped);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (filter !== 'all' && m.status !== filter) return false;
      if (!q) return true;
      return [m.name, m.email, m.position, m.department].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [members, search, filter]);

  const outCount = members.filter((m) => m.status === 'on_leave').length;
  const deptCount = new Set(members.map((m) => m.department).filter(Boolean)).size;

  const stats = [
    { label: t('team.team_size', { defaultValue: 'Team size' }), value: members.length, icon: <Users className="w-5 h-5" /> },
    { label: t('team.active', { defaultValue: 'Active' }), value: members.length - outCount, icon: <UserCheck className="w-5 h-5" /> },
    { label: t('team.out', { defaultValue: 'Out of office' }), value: outCount, icon: <CalendarOff className="w-5 h-5" /> },
    { label: t('team.departments', { defaultValue: 'Departments' }), value: deptCount, icon: <Building2 className="w-5 h-5" /> },
  ];

  return (
    <div className="space-y-6 relative">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-transparent to-amber-50/30 pointer-events-none dark:opacity-0" />

      <div className="relative z-10">
        <h1 className="text-h2 text-charcoal">{t('team.title', { defaultValue: 'Team Management' })}</h1>
        <p className="text-muted-foreground mt-1">{t('team.subtitle', { defaultValue: 'The people who report to you.' })}</p>
      </div>

      <div className="relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-orange-100 dark:border-orange-500/20">
            <div className="p-5 flex items-center gap-4">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
                {s.icon}
              </span>
              <div className="min-w-0">
                <div className="text-2xl font-bold text-foreground leading-tight">{s.value}</div>
                <div className="text-sm text-muted-foreground">{s.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="relative z-10 border-orange-100 dark:border-orange-500/20">
        <CardHeader className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Users className="w-5 h-5" /> {t('team.members', { defaultValue: 'Team members' })} ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400 w-4 h-4" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('team.search', { defaultValue: 'Search team…' })}
                className="pl-10 border-orange-200 dark:border-orange-500/25 focus:border-orange-400 focus:ring-orange-400"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(['all', 'active', 'on_leave'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    filter === f
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'border-border text-muted-foreground hover:text-orange-600 hover:border-orange-200'
                  }`}
                >
                  {f === 'all' ? t('team.filter_all', { defaultValue: 'All' }) : f === 'active' ? t('team.active', { defaultValue: 'Active' }) : t('team.on_leave', { defaultValue: 'On leave' })}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('team.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              {members.length === 0 ? t('team.empty', { defaultValue: 'No team members report to you yet.' }) : t('team.no_match', { defaultValue: 'No matching team members.' })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((m) => (
                <div
                  key={m.id}
                  onClick={() => goToEmployee(m.id)}
                  className="cursor-pointer rounded-xl border border-border bg-card p-4 hover:border-orange-300 dark:hover:border-orange-500/40 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12 border-2 border-orange-200 dark:border-orange-500/25">
                      {m.avatar ? <AvatarImage src={m.avatar} alt={m.name} /> : null}
                      <AvatarFallback className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">{initials(m.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground truncate">{m.name}</div>
                      <div className="text-sm text-muted-foreground truncate">{m.position || '—'}</div>
                    </div>
                    <Badge dot variant={m.status === 'active' ? 'success' : 'warning'}>
                      {m.status === 'active' ? t('team.active', { defaultValue: 'Active' }) : t('team.on_leave', { defaultValue: 'On leave' })}
                    </Badge>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground flex-1 min-w-0">
                      {m.department && (
                        <>
                          <Building2 className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{m.department}</span>
                        </>
                      )}
                    </span>
                    <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={`mailto:${m.email}`}
                        aria-label={`Email ${m.name}`}
                        title={t('team.email', { defaultValue: 'Email' })}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-[8px] border border-border text-muted-foreground hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:hover:bg-orange-500/[0.12] transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                      {m.phone && (
                        <a
                          href={`tel:${m.phone}`}
                          aria-label={`Call ${m.name}`}
                          title={t('team.call', { defaultValue: 'Call' })}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-[8px] border border-border text-muted-foreground hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:hover:bg-orange-500/[0.12] transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamManagementPage;
